import React from "react";
import { Section, Reveal, Glass } from "@/components/network/NetworkAtoms";
import { PrimaryCta, SecondaryCta } from "./FundingCta";
import { FileSpreadsheet, Truck, Banknote, Building2, CreditCard, Receipt, Landmark, HardHat, Layers } from "lucide-react";

/**
 * The routes we place, in plain terms. Every card carries the same three lines so the grid reads
 * evenly. Figures describe what the UK market typically does — not an offer from us.
 */
const PRODUCTS = [
  {
    Icon: Banknote, name: "Term debt & working capital",
    line: "A facility drawn for a set term, repaid to a schedule you can plan around.",
    body: "The right answer when the number and the reason are both known. Unsecured to a point, then funders "
        + "price in security, debentures or guarantees — and the sizing follows turnover and serviceability.",
    facts: [["Typical size", "£25k – £10m"], ["Typical term", "1–6 years"], ["Best for", "Defined spend, planned repayment"]],
  },
  {
    Icon: FileSpreadsheet, name: "Invoice finance",
    line: "Convert your sales ledger into working capital as you raise it.",
    body: "The funder advances the majority of each invoice on issue and settles the balance when your customer "
        + "pays. The facility scales with turnover, which is why it outlasts most term loans.",
    facts: [["Typical advance", "80–90% per invoice"], ["Facility size", "£50k – £50m"], ["Best for", "30–90 day payment terms"]],
  },
  {
    Icon: Truck, name: "Asset finance & refinance",
    line: "Fund plant, vehicles, machinery — or release the value already in them.",
    body: "The asset carries the security, so appetite holds where unsecured lending thins out. Hire purchase to "
        + "own it outright, lease to hand it back, refinance to put capital back on the balance sheet.",
    facts: [["Typical size", "£25k – £50m"], ["Typical term", "1–7 years"], ["Best for", "Capex, fleet, plant, refinance"]],
  },
  {
    Icon: Building2, name: "Bridging & short-term secured",
    line: "Secured money against property, priced by the month.",
    body: "Auction purchases, chain breaks, refinancing against the clock, or holding a position until a sale or "
        + "term facility completes. Every funder underwrites the exit first, so the exit is where we start.",
    facts: [["Typical size", "£100k – £100m"], ["Typical term", "3–24 months"], ["Best for", "Speed and certainty of completion"]],
  },
  {
    Icon: Landmark, name: "Commercial mortgages",
    line: "Long-term debt on premises you own or are acquiring.",
    body: "Owner-occupied industrial, offices, retail, healthcare and mixed-use — purchase, refinance or equity "
        + "release. Valuation and legals set the timetable, so early engagement is worth real money.",
    facts: [["Typical size", "£250k – £100m"], ["Typical term", "5–25 years"], ["Best for", "Acquiring or refinancing property"]],
  },
  {
    Icon: HardHat, name: "Development & construction",
    line: "Staged funding against land, build costs and professional fees.",
    body: "Residential, commercial and mixed-use schemes, drawn in tranches against monitored surveyor reports. "
        + "Gearing follows GDV and cost, and the funder's confidence follows the team's track record.",
    facts: [["Typical size", "£500k – £150m"], ["Typical term", "9–36 months"], ["Best for", "Land, build and refurbishment"]],
  },
  {
    Icon: Layers, name: "Acquisition & structured debt",
    line: "Senior, unitranche and mezzanine debt for the larger transaction.",
    body: "Management buy-outs, bolt-on acquisitions, shareholder reorganisations and growth capital — structured "
        + "with debt funds, clearing banks and private credit, including club and syndicated positions.",
    facts: [["Typical size", "£5m – £500m+"], ["Typical term", "3–7 years"], ["Best for", "M&A, MBO, growth capital"]],
  },
  {
    Icon: CreditCard, name: "Revenue-based & merchant advances",
    line: "Repayment that moves with your takings rather than a fixed date.",
    body: "An advance recovered as an agreed share of card or bank receipts, so a quiet month costs less than a "
        + "strong one. Common in retail, hospitality, leisure and anything with a genuine season.",
    facts: [["Typical size", "£25k – £2m"], ["Typical speed", "Often within 24 hours"], ["Best for", "Seasonal and card-led trade"]],
  },
  {
    Icon: Receipt, name: "VAT, tax & seasonal facilities",
    line: "Short facilities for the liabilities that arrive at the wrong moment.",
    body: "Quarterly VAT, corporation tax, duty and stock build, spread over months instead of landing in one "
        + "week — without drawing down the working capital the business is actually trading on.",
    facts: [["Typical size", "£25k – £2m"], ["Typical term", "3–12 months"], ["Best for", "VAT, tax, duty, stock peaks"]],
  },
];

export default function ProductCards({ lede }) {
  return (
    <Section
      testId="funding-products"
      eyebrow="What we place"
      title="You don't need to arrive knowing which facility you want"
      lede={lede
        || "Most enquiries open with a number and a reason, not a product name. Nine routes, £25,000 to £500m and "
         + "beyond, any sector and any purpose — and frequently two of them structured together."}
    >
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.05}>
            <Glass className="h-full p-7 flex flex-col" testId={`product-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p.Icon className="size-5 text-[#7da9ff]" />
              <h3 className="mt-5 font-cabinet text-[21px] font-semibold text-white tracking-[-0.02em] leading-tight">{p.name}</h3>
              <p className="mt-2 text-[14.5px] font-medium text-slate-200 leading-snug">{p.line}</p>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-400 flex-1">{p.body}</p>
              <dl className="mt-6 space-y-2 border-t border-white/10 pt-4">
                {p.facts.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-4">
                    <dt className="text-[11.5px] uppercase tracking-[0.12em] text-slate-500 shrink-0">{k}</dt>
                    <dd className="text-[13px] text-slate-200 text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </Glass>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <PrimaryCta testId="products-primary" />
          <SecondaryCta testId="funding-products-desk" label="Find your route in three taps" target="funding-eligibility" />
        </div>
        <p className="mt-5 text-[11.5px] text-slate-500 max-w-2xl leading-relaxed">
          Figures describe what is typical across the UK market rather than an offer from us. Pricing, gearing and
          terms are set by the funder and depend on the business, the security and the transaction.
        </p>
      </Reveal>
    </Section>
  );
}
